<template>
  <div>
    <!-- ============================================================== -->
    <!-- Container fluid  -->
    <!-- ============================================================== -->
    <div class="container-fluid">
      <!-- ============================================================== -->
      <!-- Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row page-titles">
        <div class="col-md-5 col-8 align-self-center">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/home">Home</router-link>
            </li>
            <li class="breadcrumb-item active">固有コード</li>
          </ol>
        </div>
      </div>
      <!-- ============================================================== -->
      <!-- End Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row">
        <div class="col-12">
          <!-- Column -->
          <div class="card">
            <div class="card-body">
              <div class="">
                <div class="row">
                  <div class="col-md-8">
                    <label class="control-label">新規コード</label>
                    <div class="form-inline">
                      <div class="form-group" :class="{ 'has-danger': errors.service_code }">
                        <input type="text" class="form-control" style="width: 180px;" name="service_code"
                          v-validate="'required'" data-vv-as="サービスコード" data-vv-validate-on="none" v-model="service_code"
                          placeholder="サービスコード">
                      </div>
                      <div class="form-group" :class="{ 'has-danger': errors.display_name }">
                        <input type="text" class="form-control ml-2" style="width: 400px;" name="display_name"
                          v-validate="'required'" data-vv-as="コード名" data-vv-validate-on="none" v-model="display_name"
                          placeholder="コード名(20文字以内)">
                      </div>
                    </div>
                    <div class="form-actions">
                      <div class="row p-t-10 p-b-10">
                        <div class="col-md-12 text-left">
                          <button type="button" v-on:click="newCode()" class="btn btn-success btn-add">追加する</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-5">
          <ul class="list-group">
            <draggable v-model='codes' item-key="sevice_code" v-bind="{
              animation: 200,
              delay: 50,
              filter: 'input',
              preventOnFilter: false
            }">
              <template #item="{ element }">
                <li class="mb-1 flex justify-between items-center list-group-item" style="cursor: move;">
                  <div v-if="!element.editing" class="drag text-gray-500 flex">
                    <FeatherIcon type="menu" />
                  </div>
                  <div class="flex items-center">
                    <template v-if="!element.editing">
                      <p class="h-4 ml-4 font-semibold">{{ element.service_code }}：{{ element.display_name }}</p>
                    </template>
                    <template v-else>
                      <div class="form-inline">
                        <input v-model="element.service_code" type="text" class="ml-4 form-control input-code">
                        <input v-model="element.display_name" type="text" class="ml-2 form-control input-name">
                      </div>
                    </template>
                  </div>
                  <div class="flex">
                    <button v-if="!element.editing" aria-label="Edit code" class="p-1 text-teal-500 btn-edit"
                      @click="editCode(element)">
                      <FeatherIcon type="edit" />
                    </button>
                    <button v-if="element.editing" aria-label="Update code" class="p-1 text-blue-500 btn-update"
                      @click="updateCode(element)">
                      <FeatherIcon type="check" />
                    </button>
                    <button v-if="!element.editing" aria-label="Remove code" class="p-1 text-red-500 btn-remove"
                      @click="removeCode(element)">
                      <FeatherIcon type="trash-2" />
                    </button>
                  </div>
                </li>
              </template>
            </draggable>
          </ul>
        </div>
      </div>
      <div class="row">
        <div class="col-md-5">
          <div class="form-actions">
            <div class="row p-t-10 p-b-10">
              <div class="col-md-12 text-right">
                <button type="button" :disabled="!canSave" v-if="codes.length > 0" v-on:click="save()"
                  class="btn btn-success btn-add">
                  保存する
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Column -->
      <p v-if="codes.length > 1" style="color: red;">※ドラッグして表示順を変更することができます。</p>
    </div>
  </div>
</template>

<script setup>
import _ from 'underscore'
import api from '@/libs/Api'
import repos from '@/libs/Repository'
import FeatherIcon from 'vue-feather'
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useExecutor } from '@/libs/Executor'
import { useToast } from 'vue-toastification'
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import draggable from 'vuedraggable'

const store = useStore()
const { exec } = useExecutor()
const toast = useToast()

const SERVICE_TYPE_UNIQUE_CODE = 1190
const canSave = ref(true)
const codes = ref([])
const newNo = ref(0)
const isProcessing = ref(false)

const { handleSubmit, errors, defineField } = useForm({
  validationSchema: {
    // eslint-disable-next-line no-template-curly-in-string
    service_code: yup.string().label('サービスコード').required('${label}を入力してください。'),
    // eslint-disable-next-line no-template-curly-in-string
    display_name: yup.string().label('コード名').required('${label}を入力してください。')
  }
})

// eslint-disable-next-line camelcase
const [service_code] = defineField('service_code')

// eslint-disable-next-line camelcase
const [display_name] = defineField('display_name')

const getServiceTypes = computed(() => {
  return store.getters['session/getServiceTypes']
})

const load = async () => {
  await reloadServiceCodeAsync()
  const serviceTypes = _.find(getServiceTypes.value, (serviceType) => {
    return (serviceType.id === Number(SERVICE_TYPE_UNIQUE_CODE))
  })
  let tempCodes = serviceTypes.service_unique_codes.map(code => ({
    ...code,
    editing: false
  }))

  codes.value = tempCodes
}

onMounted(() => {
  load()
})

const save = () => {
  let param = []
  _.each(codes.value, (code, i) => {
    let p = _.clone(code)
    delete p.editing
    p.sort_order = i
    param.push(p)
  })

  exec(async () => {
    if (isProcessing.value) return
    isProcessing.value = true
    const res = await api.postUniqueCode(param)
    if (!res) return Promise.resolve()

    let serviceTypes = getServiceTypes.value
    _.each(serviceTypes, (serviceType) => {
      if (serviceType.id === Number(SERVICE_TYPE_UNIQUE_CODE)) {
        serviceType.service_unique_codes = res
      }
    })

    codes.value = res
    repos.setServiceTypes(serviceTypes)
    toast.success('保存しました。')

    isProcessing.value = false
  }).catch(() => {
  })
}

// eslint-disable-next-line no-unused-vars
const completeCode = (code) => {
  code.completed = true
}

const newCode = () => {
  handleSubmit((value) => {
    if (service_code.value === '' || display_name.value === '') return
    if (!isServiceCodeDuplicate(service_code.value)) {
      toast.error('サービスコードが重複しています。')
      return
    }

    let no = 0
    if (codes.value.length > 0) {
      no = Math.max.apply(null, codes.value.map((code) => {
        return code.sort_order
      })) + 1

      newNo.value = newNo.value < no ? no : newNo.value
    }

    codes.value.push({
      sort_order: newNo.value,
      service_code: service_code.value,
      display_name: display_name.value,
      editing: false
    })

    service_code.value = ''
    display_name.value = ''
  })()
}

const isServiceCodeDuplicate = (serviceCode) => {
  return !_.findWhere(codes.value, { service_code: serviceCode })
}

const removeCode = (code) => {
  if (code.id !== undefined) {
    exec(async () => {
      if (isProcessing.value) return
      isProcessing.value = true
      const res = await api.getCheckUniqueCode(code.id)

      if (res > 0) {
        toast.error('ケアプランで使用されているコードは削除できません。')
      } else {
        codes.value.splice(codes.value.indexOf(code), 1)
      }

      isProcessing.value = false

      return Promise.resolve()
    }).catch(() => {
    })
  } else {
    codes.value.splice(codes.value.indexOf(code), 1)
  }
}

const editCode = (code) => {
  code.editing = true
  canSave.value = false
}

const updateCode = (code) => {
  if (code.display_name === '' || code.service_code === '') return
  if (!isServiceCodeDuplicate(code)) {
    toast.error('サービスコードが重複しています。')
    return
  }

  code.editing = false
  const editing = _.findWhere(codes.value, { editing: true })
  canSave.value = editing === undefined
}

const charaLimit = (text) => {
  return text.length > 20 ? text.slice(0, 20) : text
}

watch(display_name, (newValue) => {
  const limitedText = charaLimit(newValue)
  if (limitedText !== display_name.value) {
    display_name.value = limitedText
  }
})

const reloadServiceCodeAsync = () => store.dispatch('session/reloadServiceCodeAsync')

</script>

<style lang="scss" scoped>
.list-group-item {
  padding: 0.5rem 0.5rem !important;
}

.justify-between {
  justify-content: space-between;
}

.items-center {
  align-items: center;
  flex: 1;
  overflow: hidden;
}

.flex {
  display: flex;
}

.text-gray-500 {
  color: #909090;
}

.text-teal-500 {
  color: #38b2ac;
}

.text-blue-500 {
  color: #1919ff;
}

.text-red-500 {
  color: #f56565;
}

.input-code {
  width: 130px;
}

.input-name {
  width: 350px;
}

.btn-default {
  color: #333;
  background-color: #fff;
  border-color: #ccc;
}

.btn-add {
  width: 200px;
}

.btn-edit,
.btn-update,
.btn-remove {
  box-sizing: border-box;
  border-width: 0;
  border-style: solid;
  border-color: #e2e8f0;
  background-color: #ffffff;
}

.btn-edit:focus,
.btn-update:focus,
.btn-remove:focus {
  outline: none;
  background: #ffffff;
}

.pull-right {
  float: right !important;
}

.h-4 {
  height: 4px;
}
</style>
